/**
 * Generic helpers for the "breathing" idle drift animation used across
 * the project (Lineup player labels, Commentary Special card icons, …).
 *
 * Each element drifts along a soft elliptical path defined by the
 * `@keyframes breathe` in `./breathe.css`, which is imported below as a
 * side-effect so any consumer that does
 * `import { breatheParams } from '$utils/breathe'` automatically picks
 * up both the JS helper AND the supporting global CSS — no separate
 * stylesheet wiring needed.
 *
 * Usage in any component:
 *
 *   import { breatheParams } from '$utils/breathe';
 *
 *   {@const b = breatheParams(row, col)}
 *   <div
 *     class="breathe"
 *     style:--breathe-delay={b.delay}
 *     style:--breathe-dur={b.dur}
 *   >…</div>
 *
 * The visual variety comes from giving every element its own
 * `animation-duration` and a negative `animation-delay` so they all
 * start mid-cycle out of phase with each other.
 *
 * The math here is intentionally tiny and deterministic — no JS state,
 * no `Math.random()` — so server-rendered markup matches what the
 * client hydrates with.
 */

// Side-effect import: ships the global @keyframes + `.breathe` rules.
import './breathe.css';

/** Duration range for the breathing cycle, in seconds. */
const DUR_MIN = 7;
const DUR_RANGE = 3;

/** Phase-offset range, in seconds. ~Larger than DUR_MIN + DUR_RANGE so
 * any cycle position is reachable. */
const DELAY_RANGE = 9;

/**
 * Deterministic pseudo-random in `[0, 1)` seeded from two integers.
 * Uses the well-known `sin` hash — cheap, dependency-free, and stable
 * across runs.
 *
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export const seed = (a, b) => {
  const x = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * Compute breathing animation parameters for a single element.
 *
 * Add the `breathe` class to opt in to the global animation (defined in
 * `./breathe.css`, imported above), then pass these values through as
 * inline CSS custom properties on the element:
 *
 * ```svelte
 * {@const b = breatheParams(row, col)}
 * <div
 *   class="breathe"
 *   style:--breathe-delay={b.delay}
 *   style:--breathe-dur={b.dur}
 * >…</div>
 * ```
 *
 * @param {number} row - row index (0..n) within a logical group
 * @param {number} col - element index (0..n) within its row
 * @param {number} [salt=0] - extra integer to decorrelate two groups
 *   (e.g. mirrored halves) so they don't accidentally animate in lockstep.
 * @returns {import('./index').BreatheParams}
 */
export const breatheParams = (row, col, salt = 0) => {
  const a = row + 1 + salt;
  const b = col + 1;
  const delay = -(seed(a, b) * DELAY_RANGE);
  const dur = DUR_MIN + seed(a + 1, b + 4) * DUR_RANGE;
  return {
    delay: `${delay.toFixed(2)}s`,
    dur: `${dur.toFixed(2)}s`,
  };
};
